import type { Id } from 'convex/_generated/dataModel';
import type { Garden } from '@modules/garden-planner/business_logic';
import type { ConfirmRequest } from '@modules/garden-planner/models';

export function createHandleSizeChange(
  selectedGarden: Garden | null | undefined,
  updateGardenSize: (args: {
    gardenId: Id<'gardens'>;
    rows: number;
    columns: number;
  }) => Promise<unknown>
) {
  return async (rows: number, columns: number) => {
    if (!selectedGarden) return;
    await updateGardenSize({ gardenId: selectedGarden._id, rows, columns });
  };
}

export function createHandleAddRow(
  selectedGarden: Garden | null | undefined,
  updateGardenSize: (args: {
    gardenId: Id<'gardens'>;
    rows: number;
    columns: number;
  }) => Promise<unknown>
) {
  return async () => {
    if (!selectedGarden) return;
    await updateGardenSize({
      gardenId: selectedGarden._id,
      rows: selectedGarden.rows + 1,
      columns: selectedGarden.columns,
    });
  };
}

export function createHandleConfirmedRemoveRow(
  selectedGarden: Garden | null | undefined,
  updateGardenSize: (args: {
    gardenId: Id<'gardens'>;
    rows: number;
    columns: number;
  }) => Promise<unknown>
) {
  return async () => {
    if (!selectedGarden || selectedGarden.rows <= 1) return;
    await updateGardenSize({
      gardenId: selectedGarden._id,
      rows: selectedGarden.rows - 1,
      columns: selectedGarden.columns,
    });
  };
}

export function createHandleAddColumn(
  selectedGarden: Garden | null | undefined,
  updateGardenSize: (args: {
    gardenId: Id<'gardens'>;
    rows: number;
    columns: number;
  }) => Promise<unknown>
) {
  return async () => {
    if (!selectedGarden) return;
    await updateGardenSize({
      gardenId: selectedGarden._id,
      rows: selectedGarden.rows,
      columns: selectedGarden.columns + 1,
    });
  };
}

export function createHandleConfirmedRemoveColumn(
  selectedGarden: Garden | null | undefined,
  updateGardenSize: (args: {
    gardenId: Id<'gardens'>;
    rows: number;
    columns: number;
  }) => Promise<unknown>
) {
  return async () => {
    if (!selectedGarden || selectedGarden.columns <= 1) return;
    await updateGardenSize({
      gardenId: selectedGarden._id,
      rows: selectedGarden.rows,
      columns: selectedGarden.columns - 1,
    });
  };
}

export function getPlantsInRow(selectedGarden: Garden | null | undefined, row: number) {
  if (!selectedGarden) return 0;
  return selectedGarden.plants.filter((p) => p.row === row).length;
}

export function getPlantsInColumn(selectedGarden: Garden | null | undefined, col: number) {
  if (!selectedGarden) return 0;
  return selectedGarden.plants.filter((p) => p.col === col).length;
}

export function createHandleRemoveRow(
  selectedGarden: Garden | null | undefined,
  handleConfirmedRemoveRow: () => Promise<void>,
  onConfirm: (request: ConfirmRequest) => void,
  getConfirmCopy: (plantsInLastRow: number) => Pick<ConfirmRequest, 'title' | 'message'>
) {
  return async () => {
    if (!selectedGarden) return;
    const plantsInLastRow = getPlantsInRow(selectedGarden, selectedGarden.rows - 1);
    if (plantsInLastRow > 0) {
      onConfirm({
        ...getConfirmCopy(plantsInLastRow),
        onConfirm: handleConfirmedRemoveRow,
      });
    } else {
      await handleConfirmedRemoveRow();
    }
  };
}

export function createHandleRemoveColumn(
  selectedGarden: Garden | null | undefined,
  handleConfirmedRemoveColumn: () => Promise<void>,
  onConfirm: (request: ConfirmRequest) => void,
  getConfirmCopy: (plantsInLastCol: number) => Pick<ConfirmRequest, 'title' | 'message'>
) {
  return async () => {
    if (!selectedGarden) return;
    const plantsInLastCol = getPlantsInColumn(selectedGarden, selectedGarden.columns - 1);
    if (plantsInLastCol > 0) {
      onConfirm({
        ...getConfirmCopy(plantsInLastCol),
        onConfirm: handleConfirmedRemoveColumn,
      });
    } else {
      await handleConfirmedRemoveColumn();
    }
  };
}
